'use client';

import React from 'react';
import dayjs from 'dayjs';
import { useParams, useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';

import client from '@/clients/customer/api-client';
import HeadlessModal from '@/components/shared/HeadlessModal';
import OverallRatingCard, {
  FoodIcon,
} from '@/components/store/OverallRatingCard';

import type { AvgTotalRating } from '@/lib/types/customer/restaurant/AvgTotalRating';
import { DeliveryMini, Location, Pickup } from '@ui/Icons';

import Button from '@ui/Button';
import CustomPopup from '@/components/shared/CustomPopup';
import { usePopupStore } from '@/stores/popup';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import AnimatedCheckbox from '@/components/shared/AnimatedCheckbox';
import { useTranslations } from 'next-intl';
import { COMMON, STOREFRONT } from '@/lib/utils/translation';
import RatingStarInput from '@/components/shared/RatingStarInput';

type Props = {
  avgTotalRating: AvgTotalRating;
  storeName: string;
};

export default function PostReview({ avgTotalRating, storeName }: Props) {
  const [foodRating, setFoodRating] = React.useState<number>(0);
  const [serviceRating, setServiceRating] = React.useState<number>(0);
  const [deliveryRating, setDeliveryRating] = React.useState<number>(0);
  const [locationRating, setLocationRating] = React.useState<number>(0);
  const [comment, setComment] = React.useState<string>('');
  const [isAnnonymous, setIsAnnonymous] = React.useState<boolean>(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [stage, setStage] = React.useState<'review' | 'success'>('review');

  const order_id = useSearchParams().get('order_id');
  const order_token = useSearchParams().get('order_token');
  const created_at = useSearchParams().get('created_at');
  // const name = useSearchParams().get('name');
  const delivery = useSearchParams().get('delivery');
  const locale = useParams().locale;
  const lang = locale.slice(0, 2) as string;
  const t = useTranslations();

  const [showModal, setShowModal] = React.useState<boolean>(
    !!order_id && !!order_token
  );

  // const isShowPopup = useStore(usePopupStore, (state) => state.show);
  const closePopup = usePopupStore((state) => state.closePopup);

  const day = dayjs
    .unix(Number(created_at))
    .locale(lang)
    .format('DD MMM YYYY; HH:mm');

  const showPopup = usePopupStore((state) => state.showPopup);

  const closeModal = () => {
    setShowModal(false);
  };

  const handleIsAnnonymous = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setIsAnnonymous(evt.target.value === '1');
  };

  const handleSubmit = () => {
    setIsLoading(true);
    closePopup?.();
    if (foodRating === 0 || comment === '') {
      showPopup('error', t(`${STOREFRONT.STORE_PROFILE}.review_error`));
    }

    const payload: { [key: string]: string | number | boolean } = {
      review: comment,
      food_rating: foodRating,
      is_annonymous: isAnnonymous,
    };

    if (serviceRating !== 0) {
      payload['service_rating'] = serviceRating;
    }
    if (deliveryRating !== 0) {
      payload['delivery_rating'] = deliveryRating;
    }
    if (locationRating !== 0) {
      payload['location_rating'] = locationRating;
    }

    client
      .post(`/guest-user/orders/${order_id}/${order_token}/review`, payload)
      .then((res) => {
        if (res.status == 201) {
          // setStage('success');
          toast.success(t(`${STOREFRONT.STORE_PROFILE}.review_submitted`));
          const url = new URL(window.location.href);
          url.search = '';
          window.location.href = url.toString();
        }
      })
      .catch((err) => {
        showPopup('error', err.response.data.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!showModal) return null;

  return (
    <HeadlessModal
      isOpen={showModal}
      closeModal={closeModal}
      dialogBodyClass="overflow-y-auto"
    >
      <h3 className="text-h3 font-[900] leading-[140%] text-title">
        {t(`${COMMON}.reviews`)}
      </h3>
      <OverallRatingCard
        avgTotalRating={avgTotalRating}
        rounded
        storeName={storeName}
      />
      <div className="py-[40px]">
        <h3 className="text-h5 font-bold text-title">
          {t(`${COMMON}.order`)}# {order_id}
        </h3>
        <h4 className="text-lg text-paragraph ">
          {day}
          {/* 03 July 2023 at 4:16 PM */}
        </h4>
      </div>
      {stage === 'success' && (
        <div>
          <AnimatedCheckbox />
          <h2 className="mt-[5px] text-center text-h4 font-semibold">
            {t(`${STOREFRONT.STORE_PROFILE}.review_submitted`)}
          </h2>
        </div>
      )}
      {stage === 'review' && (
        <>
          <div className="flex flex-col gap-y-2 text-neutral-400">
            <div className="flex items-center gap-x-[5px]">
              <FoodIcon />{' '}
              <h4 className="font-bold text-title">
                {t(`${STOREFRONT.STORE_PROFILE}.food`)}
              </h4>
              <RatingStarInput rating={foodRating} setRating={setFoodRating} />
            </div>
            <span className="flex gap-x-[5px]">
              <Pickup />{' '}
              <h4 className="font-bold text-title">
                {' '}
                {t(`${STOREFRONT.STORE_PROFILE}.service`)}
              </h4>
              <RatingStarInput
                rating={serviceRating}
                setRating={setServiceRating}
              />
            </span>
            <span className="flex gap-x-[5px]">
              <DeliveryMini />{' '}
              <h4 className="font-bold text-title">
                {delivery === '1'
                  ? t(`${COMMON}.delivery`)
                  : t(`${COMMON}.pickup`)}
                {/* Delivery/Pickup */}
              </h4>
              <RatingStarInput
                rating={deliveryRating}
                setRating={setDeliveryRating}
              />
            </span>
            <span className="flex gap-x-[5px]">
              <Location />{' '}
              <h4 className="font-bold text-title">
                {t(`${STOREFRONT.STORE_PROFILE}.location`)}
              </h4>
              <RatingStarInput
                rating={locationRating}
                setRating={setLocationRating}
              />
            </span>
          </div>
          <div className="py-[20px]">
            <label
              htmlFor="is_annonymous"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              {t(`${STOREFRONT.STORE_PROFILE}.show_name`)}
            </label>
            <select
              id="is_annonymous"
              name="is_annonymous"
              className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
              defaultValue={'1'}
              onChange={handleIsAnnonymous}
            >
              <option value={'1'}>{t(`${COMMON}.anonymous`)}</option>
              <option value={'2'}>
                {' '}
                {t(`${STOREFRONT.STORE_PROFILE}.show_name`)}{' '}
              </option>
            </select>
          </div>
          <div className="pb-[20px]">
            <label
              htmlFor="comment"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              {t(`${STOREFRONT.STORE_PROFILE}.add_comment`)}
            </label>
            <div className="mt-2">
              <textarea
                rows={4}
                name="comment"
                id="comment"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t(`${STOREFRONT.STORE_PROFILE}.tell_experience`)}
              />
            </div>
          </div>
          <CustomPopup />
          <Button
            gradient
            rounded
            onClick={handleSubmit}
            disabled={isLoading}
            className="group"
          >
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              t(`${STOREFRONT.STORE_PROFILE}.submit_review`)
            )}
          </Button>
        </>
      )}
    </HeadlessModal>
  );
}
